import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowUpRight, Check, Copy, FileText, Share2 } from "lucide-react";
import { api, errText } from "../lib/api";
import { toast } from "sonner";
import { acresPhrase } from "../lib/units";
import { Reveal, useSeo } from "../lib/ui";
import { StartAgain } from "../components/StartAgain";
import { VerifyChip } from "../components/DataLabel";

const ksh = (n, none = "Nothing") => (n ? `KSh ${Math.round(n).toLocaleString()}` : none);
const LOCATIONS = {
  en: [["cbd", "City centre or CBD"], ["urban", "Urban, inside the town"],
    ["peri_urban", "Peri-urban, edge of town"], ["satellite", "Satellite town or growth corridor"],
    ["rural", "Rural or agricultural"]],
  sw: [["cbd", "Katikati ya jiji (CBD)"], ["urban", "Mjini, ndani ya mji"],
    ["peri_urban", "Ukingoni wa mji"], ["satellite", "Mji wa pembezoni au ukanda unaokua"],
    ["rural", "Mashambani au kilimo"]],
};

/** One page a landowner can forward: what the idle land is worth, what it earns, what fits, what to do next. */
export default function IdleAcre() {
  const [params, setParams] = useSearchParams();
  const [counties, setCounties] = useState([]);
  const county = params.get("county") || "Kisumu";
  const acres = params.get("acres") || "1";
  const location = params.get("location") || "peri_urban";
  const lang = params.get("lang") === "sw" ? "sw" : "en";
  const [d, setD] = useState(null);
  const [email, setEmail] = useState("");
  const [alerts, setAlerts] = useState(false);
  const [state, setState] = useState({ status: "idle" });
  const [copied, setCopied] = useState(false);

  useSeo({
    title: "What Your Idle Acre Is Worth — One Page, Free — KickStartKenyaFootball",
    description:
      "Land sitting idle earns nothing. Put a number on it: the value range for your acreage in your county, the rent it would earn on every route instead of being sold, what could be built on it, and the three things to do before anyone makes you an offer.",
  });

  const set = (k, v) => {
    const next = new URLSearchParams(params);
    next.set(k, v);
    setParams(next, { replace: true });
  };

  useEffect(() => {
    api.get("/counties").then((r) => {
      const rows = Array.isArray(r.data) ? r.data : r.data.counties || [];
      setCounties(rows.map((c) => c.county || c.name).filter(Boolean));
    }).catch(() => {});
  }, []);

  useEffect(() => {
    setState({ status: "idle" });
    api.get(`/idle-acre?county=${encodeURIComponent(county)}&acres=${parseFloat(acres) || 1}&location=${location}&lang=${lang}`)
      .then((r) => setD(r.data)).catch(() => setD(null));
  }, [county, acres, location, lang]);

  const shareUrl = useMemo(
    () => `${window.location.origin}/idle-acre?county=${encodeURIComponent(county)}&acres=${parseFloat(acres) || 1}&location=${location}&lang=${lang}`,
    [county, acres, location, lang]);

  const request = async (e) => {
    e.preventDefault();
    setState({ status: "sending" });
    try {
      const r = await api.post("/idle-acre/sheet", {
        email, county, acres: parseFloat(acres) || 1, location, consent_alerts: alerts, lang,
      });
      setState({ status: "sent", url: `${process.env.REACT_APP_BACKEND_URL}${r.data.pdf_url}` });
    } catch (err) {
      setState({ status: "error", message: errText(err) });
    }
  };

  if (!d) return <div className="min-h-[60vh] bg-black" data-testid="idle-acre-loading" />;
  const t = d.labels || {};
  const label = "mono block text-[0.6rem] uppercase tracking-[0.16em] text-zinc-400";
  const select = "mt-2 border border-white/20 bg-black px-3.5 py-2.5 text-sm text-white focus:border-ochre focus:outline-none";

  return (
    <div className="bg-black pb-24 pt-12 md:pt-16" data-testid="idle-acre-page">
      <div className="mx-auto max-w-[1400px] px-5 md:px-10">
        <Reveal>
          <div className="flex flex-wrap items-center justify-between gap-4">
            <p className="overline text-ochre">{t.eyebrow_owner}</p>
            <div className="mono flex border border-white/20" data-testid="idle-acre-language">
              {[["en", "English"], ["sw", "Kiswahili"]].map(([id, name]) => (
                <button key={id} type="button" onClick={() => set("lang", id)}
                  data-testid={`idle-acre-lang-${id}`}
                  className={`px-4 py-2 text-[0.6rem] uppercase tracking-[0.16em] ${
                    lang === id ? "bg-ochre text-black" : "text-zinc-400 hover:text-white"}`}>
                  {name}
                </button>
              ))}
            </div>
          </div>
          <h1 className="display-1 mt-5 max-w-4xl">{d.heading}</h1>
          <p className="body-copy mt-6 max-w-3xl">{d.intro}</p>
          <div className="mt-8 flex flex-wrap items-end gap-4">
            <label className="block">
              <span className={label}>{t.county}</span>
              <select value={county} onChange={(e) => set("county", e.target.value)} className={select}
                data-testid="idle-acre-county">
                {(counties.length ? counties : [county]).map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </label>
            <label className="block">
              <span className={label}>{t.acres}</span>
              <input value={acres} inputMode="decimal" data-testid="idle-acre-acres"
                onChange={(e) => set("acres", e.target.value.replace(/[^\d.]/g, ""))}
                className="mt-2 w-28 border border-white/20 bg-transparent px-3 py-2.5 text-sm text-white focus:border-ochre focus:outline-none" />
            </label>
            <label className="block">
              <span className={label}>{t.where}</span>
              <select value={location} onChange={(e) => set("location", e.target.value)} className={select}
                data-testid="idle-acre-location">
                {LOCATIONS[lang].map(([id, text]) => <option key={id} value={id}>{text}</option>)}
              </select>
            </label>
          </div>
        </Reveal>

        <section className="mt-12" data-testid="idle-acre-value">
          <div className="grid gap-px bg-zinc-800 sm:grid-cols-2 lg:grid-cols-4">
            {[[t.lower, ksh(d.low_shillings, t.nothing)], [t.worth_sell, ksh(d.mid_shillings, t.nothing)],
              [t.higher, ksh(d.high_shillings, t.nothing)],
              [t.rent_year, `${ksh(d.rent_year_shillings, t.nothing)}`]].map(([k, v], i) => (
                <div key={k} className="bg-[#0b0b0b] p-7" data-testid={`idle-acre-figure-${i}`}>
                  <p className="mono text-[0.6rem] uppercase tracking-[0.16em] text-zinc-400">{k}</p>
                  <p className={`mono mt-3 text-lg ${i === 1 ? "text-ochre" : "text-white"}`}>{v}</p>
                </div>
              ))}
          </div>
          <p className="mono mt-6 max-w-3xl border-l-2 border-maasai bg-[#0b0b0b] p-5 text-[0.6rem] uppercase leading-relaxed tracking-[0.14em] text-zinc-400"
            data-testid="idle-acre-confidence">
            <VerifyChip v="estimate" /> {d.confidence_label} · {t.range} ±{d.spread_pct}% · {d.ministry.stage_label}
          </p>
          <p className="mt-5 max-w-3xl text-sm leading-relaxed text-zinc-400" data-testid="idle-acre-value-note">
            {d.value_note} {d.ministry.note}
          </p>
        </section>

        <section className="mt-16" data-testid="idle-acre-routes">
          <p className="eyebrow">{t.routes_eyebrow}</p>
          <h2 className="display-3 mt-4">{t.routes_heading}</h2>
          <div className="mt-8 overflow-x-auto border border-zinc-900">
            <table className="w-full min-w-[760px] text-left" data-testid="idle-acre-routes-table">
              <thead>
                <tr className="border-b border-zinc-800 bg-[#0b0b0b]">
                  {[t.col_route, t.col_day_one, t.col_rent_year, t.col_over_15].map((h) => (
                    <th key={h} className="mono px-4 py-3 text-[0.6rem] uppercase tracking-[0.16em] text-zinc-300">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {d.routes.map((r) => (
                  <tr key={r.term} className="border-b border-zinc-900 align-top" data-testid={`idle-acre-route-${r.term}`}>
                    <td className="px-4 py-4">
                      <span className="block font-display text-sm uppercase text-white">{r.term_label}</span>
                      <span className="mt-1 block max-w-md text-xs leading-relaxed text-zinc-500">{r.note}</span>
                    </td>
                    <td className="mono px-4 py-4 text-xs text-white">{ksh(r.day_one_shillings, t.nothing)}</td>
                    <td className="mono px-4 py-4 text-xs text-zinc-300">{ksh(r.rent_year_shillings, t.nothing)}</td>
                    <td className="mono px-4 py-4 text-xs text-zinc-300">{ksh(r.over_15_shillings, t.nothing)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <section className="mt-16 grid gap-px bg-zinc-800 lg:grid-cols-2" data-testid="idle-acre-football">
          <div className="bg-[#0b0b0b] p-7 md:p-10">
            <p className="eyebrow">{t.whats_on_eyebrow}</p>
            <p className="mt-5 text-sm leading-relaxed text-zinc-300">{d.football}</p>
            {d.facility ? (
              <>
                <p className="mt-6 text-sm leading-relaxed text-zinc-400" data-testid="idle-acre-facility">
                  {d.facility.line}
                </p>
                <div className="mt-6 space-y-px bg-zinc-800">
                  {[[t.fits_on, `${d.facility.name} — ${acresPhrase(d.facility.acres)}`],
                    [t.take_year, ksh(d.facility.income_shillings, t.nothing)],
                    [d.facility.rent_label,
                      `${ksh(d.facility.rent_it_can_pay_shillings || d.facility.market_rent_year_shillings, t.nothing)} ${t.per_year}`]]
                    .map(([k, v]) => (
                      <div key={k} className="flex flex-wrap items-baseline justify-between gap-3 bg-black px-5 py-4">
                        <span className="mono text-[0.6rem] uppercase tracking-[0.14em] text-zinc-400">{k}</span>
                        <span className="mono text-xs text-white">{v}</span>
                      </div>
                    ))}
                </div>
              </>
            ) : (
              <p className="mt-6 text-sm leading-relaxed text-zinc-400" data-testid="idle-acre-facility-none">
                {t.facility_none}
              </p>
            )}
          </div>

          <div className="bg-[#0b0b0b] p-7 md:p-10">
            <p className="eyebrow">{t.sheet_eyebrow}</p>
            <h2 className="display-3 mt-4">{t.sheet_heading}</h2>
            <p className="mt-5 text-sm leading-relaxed text-zinc-400">{t.sheet_body}</p>
            {state.status === "sent" ? (
              <div className="mt-7" data-testid="idle-acre-sent">
                <p className="mono flex items-center gap-2 text-[0.6rem] uppercase tracking-[0.16em] text-ochre">
                  <Check size={14} /> {t.sent}
                </p>
                <a href={state.url} target="_blank" rel="noreferrer" className="btn-primary mt-6 inline-flex items-center gap-2"
                  data-testid="idle-acre-download">
                  <FileText size={14} /> {t.open_sheet}
                </a>
              </div>
            ) : (
              <form onSubmit={request} className="mt-7 space-y-4" data-testid="idle-acre-form">
                <label className="block">
                  <span className={label}>{t.your_email}</span>
                  <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" required
                    placeholder={t.email_placeholder} data-testid="idle-acre-email"
                    className="mt-2 w-full border border-white/20 bg-transparent px-3.5 py-3 text-sm text-white focus:border-ochre focus:outline-none" />
                </label>
                <label className="flex items-start gap-3 text-xs leading-relaxed text-zinc-400">
                  <input type="checkbox" checked={alerts} onChange={(e) => setAlerts(e.target.checked)}
                    data-testid="idle-acre-consent-alerts" className="mt-0.5" />
                  <span>
                    {lang === "sw"
                      ? `Pia niambie wakati ardhi inabadilika katika ${county}. Si lazima — ukurasa unatumwa kwa
                         vyovyote, na kila taarifa ina kiungo cha kusitisha.`
                      : `Also tell me when land moves in ${county}. Optional — the sheet is emailed either way,
                         and every alert has a stop link.`}
                  </span>
                </label>
                <button type="submit" disabled={state.status === "sending"} data-testid="idle-acre-submit"
                  className="btn-primary inline-flex items-center gap-2 disabled:opacity-50">
                  <FileText size={14} />
                  {state.status === "sending" ? t.sending : t.submit}
                </button>
                {state.status === "error" ? (
                  <p className="text-xs text-maasai" data-testid="idle-acre-error">{state.message}</p>
                ) : null}
                <p className="text-xs leading-relaxed text-zinc-500">
                  {t.privacy_note}{" "}
                  <Link to="/privacy" className="text-ochre underline">{t.privacy_link}</Link>.
                </p>
              </form>
            )}

            <div className="mt-8 flex flex-wrap gap-3">
              <StartAgain label={t.start_again} to="/idle-acre" testId="idle-acre-start-again" />
              <a href={`https://wa.me/?text=${encodeURIComponent(`${d.share_line} ${shareUrl}`)}`}
                target="_blank" rel="noreferrer" data-testid="idle-acre-whatsapp"
                className="mono inline-flex items-center gap-2 border border-white/20 px-4 py-2.5 text-[0.6rem] uppercase tracking-[0.16em] text-white hover:border-ochre hover:text-ochre">
                <Share2 size={13} /> {t.whatsapp}
              </a>
              <button type="button" data-testid="idle-acre-copy"
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(shareUrl);
                    setCopied(true);
                    setTimeout(() => setCopied(false), 2000);
                  } catch {
                    toast.error(`Copy this link by hand: ${shareUrl}`);
                  }
                }}
                className="mono inline-flex items-center gap-2 border border-white/20 px-4 py-2.5 text-[0.6rem] uppercase tracking-[0.16em] text-white hover:border-ochre hover:text-ochre">
                {copied ? <Check size={13} /> : <Copy size={13} />} {copied ? t.copied : t.copy_link}
              </button>
            </div>
          </div>
        </section>

        <section className="mt-16 max-w-3xl border-l-2 border-maasai bg-[#0b0b0b] p-7" data-testid="idle-acre-correct-us">
          <p className="eyebrow">{t.correct_eyebrow}</p>
          <h2 className="display-3 mt-4">{t.correct_heading}</h2>
          <p className="mt-5 text-sm leading-relaxed text-zinc-300">{t.correct_body}</p>
          <Link to={`/land-comparables?county=${encodeURIComponent(county)}`}
            className="btn-primary mt-6 inline-flex items-center gap-2" data-testid="idle-acre-correct-us-cta">
            {t.correct_cta}
          </Link>
        </section>

        <section className="mt-16 max-w-3xl" data-testid="idle-acre-next">
          <p className="eyebrow">{t.next_eyebrow}</p>
          <h2 className="display-3 mt-4">{t.next_heading}</h2>
          <div className="mt-7 space-y-px bg-zinc-800">
            {d.next_steps.map((s, i) => (
              <div key={s.step} className="bg-[#0b0b0b] p-5" data-testid={`idle-acre-step-${i}`}>
                <p className="font-display text-sm uppercase text-white">{i + 1}. {s.step}</p>
                <p className="mt-2 text-xs leading-relaxed text-zinc-400">{s.why}</p>
              </div>
            ))}
          </div>
          <p className="mt-6 text-xs leading-relaxed text-zinc-500">{d.method} {d.caveat}</p>
          <div className="mono mt-8 flex flex-wrap gap-4 text-[0.6rem] uppercase tracking-[0.16em]">
            <Link to="/apply" className="text-ochre hover:text-white" data-testid="idle-acre-to-apply">{t.to_apply}</Link>
            <Link to={`/land-value/${county.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="text-ochre hover:text-white" data-testid="idle-acre-to-value">{t.to_value}</Link>
            <Link to="/land-comparables" className="text-ochre hover:text-white" data-testid="idle-acre-to-comparables">{t.to_comparables}</Link>
            <a href={d.ministry.source.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-ochre hover:text-white" data-testid="idle-acre-ministry-source">
              {d.ministry.source.name} <ArrowUpRight size={11} />
            </a>
          </div>
        </section>
      </div>
    </div>
  );
}
